/**
 * Canonical `winders/{docId}` slug derivation.
 *
 * Its own module rather than a private helper in firebase.ts for the same reason
 * `brandSlug.ts` is: the doc id is a data contract — `UserWinder.winderCatalogId`
 * FKs point at it and it can never be recomputed for an existing document — so
 * the rules live somewhere they can be read and tested without pulling in the
 * Firebase SDK.
 *
 * Deliberately NOT `deriveBrandSlug`: that one *drops* accented characters
 * rather than folding them ("Glashütte" → "glashtte") because thousands of brand
 * documents are already keyed on the lossy form. The winder catalog is new, so
 * it decomposes first and strips the combining marks — "Buben & Zörweg" becomes
 * "buben-and-zorweg", not "buben-zrweg".
 */

/** Combining diacritical marks, stripped after an NFKD decomposition. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Lower-case, hyphenated, ASCII-only slug for one component of a winder id.
 * Idempotent: an already-clean slug round-trips unchanged.
 */
export function slugifyWinderPart(value: string): string {
  return (value || '')
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .trim()
    // Ampersands carry meaning in winder make names ("Buben & Zörweg"), so they
    // become the word rather than silently vanishing into a hyphen.
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Doc id for a winder: `<make-slug>-<model-slug>`, per the shared-types
 * contract on `WinderEntry.id`.
 *
 * Falls back to whichever part is present when the other is empty, so a
 * half-filled form previews a usable id rather than a leading hyphen. Callers
 * still require both fields before saving.
 */
export function deriveWinderDocId(make: string, model: string): string {
  const makePart = slugifyWinderPart(make);
  const modelPart = slugifyWinderPart(model);
  if (!makePart) return modelPart;
  if (!modelPart) return makePart;
  // A model that already repeats its make ("Wolf Cub" under make "wolf") would
  // otherwise produce "wolf-wolf-cub".
  if (modelPart === makePart || modelPart.startsWith(`${makePart}-`)) {
    return modelPart;
  }
  return `${makePart}-${modelPart}`;
}
